import { useCallback, useSyncExternalStore } from 'react';

/**
 * Whether the digest's month card is expanded (Dan, 2026-08-18). Persisted **per device**
 * like the week-start, units and theme preferences — not in the `household` doc — because
 * "how much of my morning screen does the calendar get" is a property of the phone you're
 * holding, not of the household. Dan's desktop can keep the grid open while his phone
 * keeps it shut, and neither choice syncs over and surprises anyone else at breakfast.
 *
 * Default is **expanded**: the card was asked for, so the first sight of it should be the
 * thing itself, not a closed drawer that has to be discovered.
 */
export const DIGEST_CALENDAR_STORAGE_KEY = 'awwyeah:digest:calendar-open';

const listeners = new Set<() => void>();

function read(): boolean {
  try {
    // Only an explicit '0' collapses it — an absent key, or a value written by some older
    // build, both fall through to the expanded default rather than hiding the calendar.
    return localStorage.getItem(DIGEST_CALENDAR_STORAGE_KEY) !== '0';
  } catch {
    return true;
  }
}

export function useDigestCalendarOpen(): { open: boolean; setOpen: (open: boolean) => void } {
  const open = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      const onStorage = (e: StorageEvent): void => {
        if (e.key === DIGEST_CALENDAR_STORAGE_KEY) cb();
      };
      window.addEventListener('storage', onStorage);
      return () => {
        listeners.delete(cb);
        window.removeEventListener('storage', onStorage);
      };
    },
    read,
    () => true,
  );

  const setOpen = useCallback((next: boolean) => {
    try {
      localStorage.setItem(DIGEST_CALENDAR_STORAGE_KEY, next ? '1' : '0');
    } catch {
      /* private mode — the notify below still applies the change for this session */
    }
    for (const l of listeners) l();
  }, []);

  return { open, setOpen };
}
